// src/components/CrashCourse/FinalExam/CertificationResults.jsx
//
// Shows the outcome of a Final Exam attempt.
//  - Pass  -> confirms certification, hands off to the email/certificate flow.
//  - Fail  -> shows which topic categories to review, offers retake.

import React from "react";
import { Shield, PartyPopper, AlertTriangle } from "lucide-react";

export default function CertificationResults({ attempt, studentEmail, isDark, onRetake, onReviewModules, onContinue }) {
  const D       = isDark;
  const panel   = D ? "#142a4d" : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textMid = D ? "rgba(240,244,248,0.80)" : "#334155";
  const textSub = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  const { score, total, pct, pass, missedByCategory = {} } = attempt;
  const ringColor = pass ? "#34d399" : "#f87171";
  const catRows = Object.entries(missedByCategory).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "clamp(30px,6vw,60px) clamp(14px,4vw,28px)", textAlign: "center" }}>
      <div style={{ position: "relative", width: 150, height: 150, margin: "0 auto 22px" }}>
        <svg width="150" height="150" viewBox="0 0 36 36">
          <path fill="none" stroke={D ? "rgba(255,255,255,0.10)" : "#e2e8f0"} strokeWidth="2.6"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          <path fill="none" stroke={ringColor} strokeWidth="2.6" strokeDasharray={`${pct}, 100`} strokeLinecap="round"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 30, fontWeight: 900, color: text }}>{pct}%</div>
          <div style={{ fontSize: 12, color: textSub, fontWeight: 700 }}>{score}/{total}</div>
        </div>
      </div>

      {pass ? (
        <>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,3.4vw,28px)", fontWeight: 900, color: "#34d399", margin: "0 0 8px", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <PartyPopper size={26} /> You Passed!
          </h1>
          <p style={{ color: textMid, fontSize: 14.5, margin: "0 0 26px" }}>
            You scored {score}/{total} ({pct}%) — above the passing threshold. You are now PPCA Certified.
          </p>
          <div style={{ background: panel, border: `1px solid ${border}`, borderRadius: 14, padding: "18px 22px", marginBottom: 24, textAlign: "left", display: "flex", gap: 14, alignItems: "center" }}>
            <Shield size={22} style={{ color: goldL, flexShrink: 0 }} />
            <div style={{ fontSize: 13, color: textMid }}>
              Your <strong style={{ color: goldL }}>PPCA Certified</strong> badge has been added to your profile, and
              we're emailing your Certificate of Completion to <strong style={{ color: text }}>{studentEmail}</strong>.
            </div>
          </div>
          <button onClick={onContinue} style={btnGold(gold, goldL)}>Continue →</button>
        </>
      ) : (
        <>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,3.4vw,28px)", fontWeight: 900, color: "#f87171", margin: "0 0 8px" }}>
            Not Quite — Keep Studying
          </h1>
          <p style={{ color: textMid, fontSize: 14.5, margin: "0 0 22px" }}>
            You scored {score}/{total} ({pct}%). A higher score is required to pass.
          </p>
          {catRows.length > 0 && (
            <div style={{ background: panel, border: `1px solid ${border}`, borderRadius: 14, padding: "18px 22px", marginBottom: 22, textAlign: "left" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 13.5, color: text, marginBottom: 10 }}>
                <AlertTriangle size={15} style={{ color: "#f87171" }} /> Focus your review on these topics
              </div>
              {catRows.map(([cat, n], i) => (
                <div key={cat} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontSize: 13, color: textMid, borderBottom: i < catRows.length - 1 ? `1px solid ${border}` : "none" }}>
                  <span>{cat}</span>
                  <span style={{ color: "#f87171", fontWeight: 700 }}>{n} missed</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={onReviewModules} style={btnGhost(D)}>Review Course Modules</button>
            <button onClick={onRetake} style={btnGold(gold, goldL)}>Retake Final Exam →</button>
          </div>
        </>
      )}
    </div>
  );
}

const btnBase = {
  cursor: "pointer", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 14,
  padding: "13px 26px", display: "inline-flex", alignItems: "center", justifyContent: "center",
  gap: 8, fontFamily: "inherit",
};
function btnGold(gold, goldL) {
  return { ...btnBase, width: "100%", background: `linear-gradient(135deg,${gold},${goldL})`, color: "#0d1f3c", boxShadow: `0 4px 14px rgba(201,151,28,0.40)` };
}
function btnGhost(D) {
  return { ...btnBase, background: D ? "rgba(255,255,255,0.06)" : "#f1f5f9", color: D ? "#f0f4f8" : "#0d1f3c", border: `1px solid ${D ? "rgba(255,255,255,0.10)" : "#e2e8f0"}` };
}
